// Centralny reporter błędów. Używa fetch (nie axios), żeby uniknąć rekursji w axios interceptorze.
// Wysyła do POST /api/errors/log. Endpoint jest publiczny (rate-limited).

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

// Antyspam — to samo wiadomość+path nie częściej niż 1 raz na 5 sekund.
const _recent = new Map();
const DEDUP_MS = 5000;

function shouldSend(key) {
  const now = Date.now();
  const last = _recent.get(key);
  if (last && now - last < DEDUP_MS) return false;
  _recent.set(key, now);
  // Cleanup
  if (_recent.size > 200) {
    const cutoff = now - DEDUP_MS;
    for (const [k, t] of _recent) if (t < cutoff) _recent.delete(k);
  }
  return true;
}

function safeStringify(v, maxLen = 4000) {
  try {
    const s = typeof v === 'string' ? v : JSON.stringify(v);
    return s.length > maxLen ? s.slice(0, maxLen) + '…[truncated]' : s;
  } catch {
    return String(v).slice(0, maxLen);
  }
}

export async function reportError({
  source = 'frontend',
  level = 'error',
  message,
  stack,
  path,
  method,
  status_code,
  context,
} = {}) {
  if (!message) return;
  const _path = path || (typeof window !== 'undefined' ? window.location.pathname + window.location.search : null);
  const dedupKey = `${source}|${level}|${message?.slice?.(0, 200)}|${_path}`;
  if (!shouldSend(dedupKey)) return;

  const ctx = { ...(context || {}) };
  try {
    if (typeof window !== 'undefined') {
      ctx.url = window.location.href;
      ctx.viewport = `${window.innerWidth}x${window.innerHeight}`;
      const userJson = localStorage.getItem('user');
      if (userJson) {
        try { ctx.user = JSON.parse(userJson)?.email; } catch {}
      }
    }
  } catch {}

  const payload = {
    source,
    level,
    message: safeStringify(message, 4500),
    stack: stack ? safeStringify(stack, 18000) : null,
    path: _path ? _path.slice(0, 1900) : null,
    method: method || null,
    status_code: status_code || null,
    context: ctx,
  };

  try {
    const token = typeof localStorage !== 'undefined' ? localStorage.getItem('token') : null;
    const headers = { 'Content-Type': 'application/json' };
    if (token) headers.Authorization = `Bearer ${token}`;
    await fetch(`${BACKEND_URL}/api/errors/log`, {
      method: 'POST',
      headers,
      body: JSON.stringify(payload),
      keepalive: true,
    });
  } catch {
    // Świadome: nie chcemy rekurencyjnie logować błędu logowania błędu
  }
}

let _installed = false;
export function installGlobalErrorHandlers() {
  if (_installed || typeof window === 'undefined') return;
  _installed = true;

  window.addEventListener('error', (event) => {
    reportError({
      source: 'frontend',
      level: 'error',
      message: event.message || 'window.onerror',
      stack: event.error?.stack || `${event.filename}:${event.lineno}:${event.colno}`,
      context: {
        type: 'window.onerror',
        filename: event.filename,
        line: event.lineno,
        col: event.colno,
      },
    });
  });

  window.addEventListener('unhandledrejection', (event) => {
    const reason = event.reason;
    let msg = 'unhandledrejection';
    let stack = null;
    if (reason instanceof Error) {
      msg = reason.message || msg;
      stack = reason.stack;
    } else {
      msg = safeStringify(reason, 1000);
    }
    reportError({
      source: 'frontend',
      level: 'error',
      message: msg,
      stack,
      context: { type: 'unhandledrejection' },
    });
  });

  // Opcjonalnie: console.error podsłuchany
  const origConsoleError = console.error.bind(console);
  console.error = (...args) => {
    try {
      const first = args[0];
      const msg = first instanceof Error ? first.message : safeStringify(args.map((a) => (a instanceof Error ? a.message : a)).join(' '), 1000);
      const stack = first instanceof Error ? first.stack : null;
      reportError({
        source: 'frontend',
        level: 'error',
        message: msg,
        stack,
        context: { type: 'console.error' },
      });
    } catch {}
    return origConsoleError(...args);
  };
}
